import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TimeWheelPicker.css';

const ITEM_HEIGHT = 28;
const VISIBLE_ITEMS = 7;
const COLUMN_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS; // 196px
const SPACER_HEIGHT = ITEM_HEIGHT * 3; // 84px
const BARREL_DEG = 18; // rotateX degrees per item offset

const HOURS_12 = ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const PERIODS = ['AM', 'PM'];

const REPEATS = 10;
const MIDDLE_OFFSET = Math.floor(REPEATS / 2);

interface TimeWheelPickerProps {
  value: string; // HHMM (24h)
  anchorRef: React.RefObject<HTMLElement | null>;
  onChange?: (hhmm: string) => void;
  onConfirm: (hhmm: string) => void;
  onClose: () => void;
}

/* ── 12h ↔ 24h conversion ───────────────────────────────── */

function to12Hour(h24: number): { hourIndex: number; periodIndex: number } {
  return { hourIndex: h24 % 12, periodIndex: h24 >= 12 ? 1 : 0 };
}

function to24Hour(hourIndex: number, periodIndex: number): number {
  const h12 = hourIndex === 0 ? 12 : hourIndex;
  return periodIndex === 0 ? (h12 === 12 ? 0 : h12) : (h12 === 12 ? 12 : h12 + 12);
}

/* ── WheelColumn ─────────────────────────────────────────── */

interface WheelColumnProps {
  items: string[];
  selectedIndex: number;
  onIndexChange: (index: number) => void;
  columnRef: React.RefObject<HTMLDivElement | null>;
  width?: number;
  mono?: boolean;
  circular?: boolean;
  onWrap?: (direction: 'forward' | 'backward') => void;
}

function WheelColumn({
  items, selectedIndex, onIndexChange, columnRef,
  width = 48, mono = true, circular = false, onWrap,
}: WheelColumnProps) {
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const isInitialMount = useRef(true);
  const prevRawRef = useRef<number>(
    circular ? MIDDLE_OFFSET * items.length + selectedIndex : selectedIndex
  );

  const displayItems = circular
    ? Array.from({ length: items.length * REPEATS }, (_, i) => items[i % items.length])
    : items;

  // Barrel 3D effect — run on every scroll frame
  const updateBarrel = useCallback(() => {
    const el = columnRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const centerY = scrollTop + COLUMN_HEIGHT / 2;

    const firstIdx = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - 2);
    const lastIdx = Math.min(displayItems.length - 1, firstIdx + VISIBLE_ITEMS + 4);

    for (let i = firstIdx; i <= lastIdx; i++) {
      const child = el.children[i + 1] as HTMLElement; // +1 for top spacer
      if (!child) continue;
      const itemCenterY = SPACER_HEIGHT + i * ITEM_HEIGHT + ITEM_HEIGHT / 2;
      const offset = (itemCenterY - centerY) / ITEM_HEIGHT;
      const angle = offset * BARREL_DEG;
      child.style.transform = `perspective(250px) rotateX(${angle}deg)`;
    }
  }, [columnRef, displayItems.length]);

  // Debounced state update + snap
  const handleSnap = useCallback(() => {
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      if (!columnRef.current) return;
      const scrollTop = columnRef.current.scrollTop;
      const rawIndex = Math.round(scrollTop / ITEM_HEIGHT);

      if (circular) {
        const realIndex = ((rawIndex % items.length) + items.length) % items.length;

        if (onWrap) {
          const prevCycle = Math.floor(prevRawRef.current / items.length);
          const currentCycle = Math.floor(rawIndex / items.length);
          const wraps = currentCycle - prevCycle;
          if (wraps !== 0 && Math.abs(wraps) % 2 === 1) {
            onWrap(wraps > 0 ? 'forward' : 'backward');
          }
        }

        const targetTop = rawIndex * ITEM_HEIGHT;
        if (Math.abs(scrollTop - targetTop) > 1) {
          columnRef.current.scrollTo({ top: targetTop, behavior: 'smooth' });
        }

        prevRawRef.current = rawIndex;

        const centerRaw = MIDDLE_OFFSET * items.length + realIndex;
        if (Math.abs(rawIndex - centerRaw) > items.length * 3) {
          requestAnimationFrame(() => {
            if (!columnRef.current) return;
            columnRef.current.style.scrollBehavior = 'auto';
            columnRef.current.scrollTop = centerRaw * ITEM_HEIGHT;
            columnRef.current.style.scrollBehavior = '';
            prevRawRef.current = centerRaw;
            updateBarrel();
          });
        }

        if (realIndex !== selectedIndex) onIndexChange(realIndex);
      } else {
        const clamped = Math.max(0, Math.min(rawIndex, items.length - 1));
        const targetTop = clamped * ITEM_HEIGHT;
        if (Math.abs(scrollTop - targetTop) > 1) {
          columnRef.current.scrollTo({ top: targetTop, behavior: 'smooth' });
        }
        prevRawRef.current = clamped;
        if (clamped !== selectedIndex) onIndexChange(clamped);
      }
    }, 100);
  }, [columnRef, items.length, selectedIndex, onIndexChange, circular, onWrap, updateBarrel]);

  // Combined scroll handler: barrel every frame + debounced snap
  const handleScroll = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(updateBarrel);
    handleSnap();
  }, [updateBarrel, handleSnap]);

  // Initial scroll + barrel
  useEffect(() => {
    if (columnRef.current && isInitialMount.current) {
      const startPos = circular
        ? (MIDDLE_OFFSET * items.length + selectedIndex) * ITEM_HEIGHT
        : selectedIndex * ITEM_HEIGHT;
      columnRef.current.scrollTop = startPos;
      isInitialMount.current = false;
      requestAnimationFrame(updateBarrel);
    }
  }, [selectedIndex, columnRef, circular, items.length, updateBarrel]);

  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleItemClick = (displayIndex: number) => {
    const realIndex = circular ? displayIndex % items.length : displayIndex;
    onIndexChange(realIndex);
    columnRef.current?.scrollTo({ top: displayIndex * ITEM_HEIGHT, behavior: 'smooth' });
  };

  return (
    <div className="relative" style={{ width, height: COLUMN_HEIGHT }}>
      <div
        ref={columnRef}
        className="time-wheel-column"
        onScroll={handleScroll}
      >
        <div style={{ height: SPACER_HEIGHT, flexShrink: 0 }} />
        {displayItems.map((item, i) => (
          <div
            key={i}
            className={`time-wheel-item ${mono ? 'font-mono' : ''} ${
              (circular ? i % items.length : i) === selectedIndex
                ? 'font-semibold text-gray-900'
                : 'text-gray-400'
            }`}
            onClick={() => handleItemClick(i)}
          >
            {item}
          </div>
        ))}
        <div style={{ height: SPACER_HEIGHT, flexShrink: 0 }} />
      </div>
      <div className="time-wheel-mask" />
    </div>
  );
}

/* ── TimeWheelPicker ─────────────────────────────────────── */

export function TimeWheelPicker({ value, anchorRef, onChange, onConfirm, onClose }: TimeWheelPickerProps) {
  const parseValue = (v: string) => {
    const digits = (v || '').replace(/\D/g, '').padEnd(4, '0').slice(0, 4);
    const hh = Math.min(parseInt(digits.slice(0, 2), 10) || 0, 23);
    const mm = Math.min(parseInt(digits.slice(2, 4), 10) || 0, 59);
    return { hh, mm };
  };

  const initial = parseValue(value);
  const { hourIndex: initHourIdx, periodIndex: initPeriodIdx } = to12Hour(initial.hh);

  const [hourIndex, setHourIndex] = useState(initHourIdx);
  const [minuteIndex, setMinuteIndex] = useState(initial.mm);
  const [periodIndex, setPeriodIndex] = useState(initPeriodIdx);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [activeColumn, setActiveColumn] = useState<'hour' | 'minute' | 'period'>('hour');

  const popupRef = useRef<HTMLDivElement>(null);
  const hourColRef = useRef<HTMLDivElement>(null);
  const minuteColRef = useRef<HTMLDivElement>(null);
  const periodColRef = useRef<HTMLDivElement>(null);

  const isFirstRender = useRef(true);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Live-update the input as the user scrolls
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!onChangeRef.current) return;
    const h24 = to24Hour(hourIndex, periodIndex);
    const hh = String(h24).padStart(2, '0');
    const mm = MINUTES[minuteIndex];
    onChangeRef.current(`${hh}${mm}`);
  }, [hourIndex, minuteIndex, periodIndex]);

  // Toggle AM/PM when hours wrap past 11→12 or 12→11
  const handleHourWrap = useCallback(() => {
    setPeriodIndex(prev => {
      const next = prev === 0 ? 1 : 0;
      requestAnimationFrame(() => {
        periodColRef.current?.scrollTo({ top: next * ITEM_HEIGHT, behavior: 'smooth' });
      });
      return next;
    });
  }, []);

  // Position the popup flush below the anchor
  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popupHeight = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < popupHeight ? rect.top - popupHeight : rect.bottom;
    setPosition({ top, left: rect.left, width: rect.width });
  }, [anchorRef]);

  // Click-outside, Escape, resize, scroll listeners
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'Enter':
          e.preventDefault();
          handleConfirm();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (activeColumn === 'hour') {
            hourColRef.current?.scrollBy({ top: -ITEM_HEIGHT, behavior: 'smooth' });
          } else if (activeColumn === 'minute') {
            minuteColRef.current?.scrollBy({ top: -ITEM_HEIGHT, behavior: 'smooth' });
          } else {
            const next = Math.max(0, periodIndex - 1);
            setPeriodIndex(next);
            periodColRef.current?.scrollTo({ top: next * ITEM_HEIGHT, behavior: 'smooth' });
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (activeColumn === 'hour') {
            hourColRef.current?.scrollBy({ top: ITEM_HEIGHT, behavior: 'smooth' });
          } else if (activeColumn === 'minute') {
            minuteColRef.current?.scrollBy({ top: ITEM_HEIGHT, behavior: 'smooth' });
          } else {
            const next = Math.min(1, periodIndex + 1);
            setPeriodIndex(next);
            periodColRef.current?.scrollTo({ top: next * ITEM_HEIGHT, behavior: 'smooth' });
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setActiveColumn(prev => prev === 'period' ? 'minute' : prev === 'minute' ? 'hour' : 'hour');
          break;
        case 'ArrowRight':
          e.preventDefault();
          setActiveColumn(prev => prev === 'hour' ? 'minute' : prev === 'minute' ? 'period' : 'period');
          break;
      }
    };

    const handleResize = () => onClose();

    const handleScroll = (e: Event) => {
      if (popupRef.current && popupRef.current.contains(e.target as Node)) return;
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose, hourIndex, minuteIndex, periodIndex, activeColumn]);

  const handleConfirm = () => {
    const h24 = to24Hour(hourIndex, periodIndex);
    const hh = String(h24).padStart(2, '0');
    const mm = MINUTES[minuteIndex];
    onConfirm(`${hh}${mm}`);
  };

  const popup = (
    <div
      ref={popupRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${Math.max(position.width, 200)}px`,
        zIndex: 9999,
      }}
      className="bg-white border border-gray-200 rounded-b-xl shadow-lg px-3 py-3"
    >
      {/* Wheel columns with shared highlight band */}
      <div className="relative" style={{ height: COLUMN_HEIGHT }}>
        <div className="time-wheel-highlight" />
        <div className="flex items-center justify-center h-full">
          <WheelColumn
            items={HOURS_12}
            selectedIndex={hourIndex}
            onIndexChange={setHourIndex}
            columnRef={hourColRef}
            circular
            onWrap={handleHourWrap}
          />
          <span className="text-sm font-bold text-gray-300 select-none mx-0.5">:</span>
          <WheelColumn
            items={MINUTES}
            selectedIndex={minuteIndex}
            onIndexChange={setMinuteIndex}
            columnRef={minuteColRef}
            circular
          />
          <WheelColumn
            items={PERIODS}
            selectedIndex={periodIndex}
            onIndexChange={setPeriodIndex}
            columnRef={periodColRef}
            width={40}
            mono={false}
          />
        </div>
      </div>

      {/* Confirm button */}
      <button
        type="button"
        onClick={handleConfirm}
        className="w-full mt-2 py-1.5 bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold rounded-lg transition-colors"
      >
        Confirm
      </button>
    </div>
  );

  return createPortal(popup, document.body);
}
